import { gql } from "@apollo/client";

import CommonGoalArtifactFragment from "@graphql/common-goal-artifact-fragment";

import coreValueAssessmentQuestionFragment from "./core-value-assessment-question-fragment";

export default gql`
  ${CommonGoalArtifactFragment}
  ${coreValueAssessmentQuestionFragment}
  fragment GetAssessmentAsessmentGroupSection on AssessmentSectionInterface {
    id
    title
    description
    subjectVisibility
    questions {
      edges {
        node {
          id
          question {
            ... on IndividualGoalAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              goal {
                ...CommonGoalArtifactFragment
              }
            }
            ... on OverallGoalAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              coreValue {
                ...AssessmentQuestionCoreValue
              }
            }
            ... on OverallRecognitionCoreValueAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on RangeAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on CompetencyAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              role {
                id
                title
                level
              }
              competency {
                id
                title
                criteria {
                  edges {
                    node {
                      id
                      content
                    }
                  }
                }
              }
            }
            ... on CompetencyCriteriaAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              role {
                id
                title
                level
              }
              competencyCriteria {
                id
                content
                competency {
                  id
                  title
                  description
                }
              }
            }
            ... on ResponsibilityAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              responsibility {
                id
                content
                role {
                  id
                  title
                  level
                }
              }
            }
            ... on TalentIndicatorAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              talentIndicator {
                id
                talentIndicatorType
              }
            }
            ... on MultiChoiceAssessmentQuestionNode {
              id
              title
              description
              options
              optionDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on TextAssessmentQuestionNode {
              id
              title
              description
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on NpsAssessmentQuestionNode {
              id
              title
              description
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
          }
        }
      }
    }
  }
  query getAssessment($assessmentId: Int!, $organizationId: Int!) {
    assessment(assessmentId: $assessmentId) {
      id
      state
      submittedDatetime
      periodStartDate
      periodEndDate
      dueDate
      targetCurrentRoleIds
      canUpdate {
        permission
        reason
      }
      complianceProgram {
        id
        title
        periodStartDate
        periodEndDate
        calibration
        canReadDetails {
          permission
          reason
        }
      }
      template {
        id
        assessmentType
        delivery
        anonymity
        roleBasedAssessmentsAssessNextRole
        roleBasedAssessmentsSelfAssessNextRole
        questionSets {
          edges {
            node {
              id
              title
              sections {
                edges {
                  node {
                    ...GetAssessmentAsessmentGroupSection
                  }
                }
              }
            }
          }
        }
      }
      additionalQuestionSets {
        performance {
          edges {
            node {
              id
              title
              sections {
                edges {
                  node {
                    ...GetAssessmentAsessmentGroupSection
                  }
                }
              }
            }
          }
        }
        peer {
          edges {
            node {
              id
              title
              sections {
                edges {
                  node {
                    ...GetAssessmentAsessmentGroupSection
                  }
                }
              }
            }
          }
        }
        manager {
          edges {
            node {
              id
              title
              sections {
                edges {
                  node {
                    ...GetAssessmentAsessmentGroupSection
                  }
                }
              }
            }
          }
        }
      }
      responder {
        id
        name
        avatar
      }
      target {
        id
        name
        email
        firstName
        avatar
        organizationSpecificInfo(organizationId: $organizationId) {
          jobLevel
          startDate
        }
        managersList {
          id
        }
      }
      targetPosition
      answers {
        ... on RangeAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on IndividualGoalAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on OverallGoalAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on IndividualRecognitionCoreValueAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on OverallRecognitionCoreValueAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on ResponsibilityAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on TalentIndicatorAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on CompetencyAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on CompetencyCriteriaAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
        ... on MultiChoiceAssessmentAnswerNode {
          id
          questionId
          choices
          comment
          isLocked
        }
        ... on TextAssessmentAnswerNode {
          id
          questionId
          textAnswer
          comment
          isLocked
        }
        ... on NpsAssessmentAnswerNode {
          id
          questionId
          integerAnswer
          comment
          isLocked
        }
      }
      otherParticipantsAnswers {
        edges {
          node {
            ... on RangeAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on IndividualGoalAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on OverallGoalAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on IndividualRecognitionCoreValueAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on OverallRecognitionCoreValueAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on ResponsibilityAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on TalentIndicatorAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on CompetencyAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on CompetencyCriteriaAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on MultiChoiceAssessmentAnswerNode {
              id
              questionId
              choices
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on TextAssessmentAnswerNode {
              id
              questionId
              textAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
            ... on NpsAssessmentAnswerNode {
              id
              questionId
              integerAnswer
              comment
              responder {
                id
                name
                avatar
              }
            }
          }
        }
      }
      relatedMeetings {
        edges {
          node {
            id
            title
            meetingGroupId
            startDatetime
            endDatetime
            draft
          }
        }
      }
      mostRecentAnswers {
        questionId
        questionType
        integerAnswer
        submitted
      }
    }
  }
`;
